import { z } from 'zod';

import {
  BooleanField,
  BorrowerField,
  CurrencyField,
  EmailField,
  EnumField,
  NumberField,
  ObjectField,
  PhoneNumberField,
  PlainDateField,
  StringField,
} from './Schema';

/**
 * A Form is an enhancement to a Schema that adds presentational information.
 * Anything non-presentational should go in Schema instead (and be inherited by
 * these types via the relevant `extends`).
 */

/**
 * A select form element. Can be either a {@link ControlledSimpleSelect}, which doesn't allow typeahead
 * searches, or a {@link ControlledSelect}, which does.
 *
 */
export const SelectFormElement = z
  .object({
    ...EnumField.shape,
    elementType: z.literal('select'),
    typeaheadSearch: z.boolean().optional(),
  })
  .meta({
    description: 'Select dropdown allowing user to choose from enumerated options.',
    id: 'SelectFormElement',
  });
export type SelectFormElement = z.infer<typeof SelectFormElement>;

export const RadioFormElement = z
  .object({
    elementType: z.literal('radio'),
    ...EnumField.shape,
  })
  .meta({ description: 'Radio button group for selecting one enumerated option.', id: 'RadioFormElement' });
export type RadioFormElement = z.infer<typeof RadioFormElement>;

export const TextInputFormElement = z
  .object({
    elementType: z.literal('textInput'),
    ...StringField.shape,
  })
  .meta({ description: 'Single-line text input field.', id: 'TextInputFormElement' });
export type TextInputFormElement = z.infer<typeof TextInputFormElement>;

export const TextAreaFormElement = z
  .object({
    elementType: z.literal('textArea'),
    ...StringField.shape,
  })
  .meta({ description: 'Multi-line text area input field.', id: 'TextAreaFormElement' });
export type TextAreaFormElement = z.infer<typeof TextAreaFormElement>;

export const EmailFormElement = z
  .object({
    elementType: z.literal('email'),
    ...EmailField.shape,
  })
  .meta({ description: 'Input field specifically for email addresses.', id: 'EmailFormElement' });
export type EmailFormElement = z.infer<typeof EmailFormElement>;

export const PhoneNumberFormElement = z
  .object({
    elementType: z.literal('phoneNumber'),
    ...PhoneNumberField.shape,
  })
  .meta({ description: 'Input field for phone numbers.', id: 'PhoneNumberFormElement' });
export type PhoneNumberFormElement = z.infer<typeof PhoneNumberFormElement>;

export const NumberFormElement = z
  .object({
    elementType: z.literal('numberInput'),
  })
  .extend(NumberField.shape)
  .meta({ description: 'Numeric input field.', id: 'NumberFormElement' });
export type NumberFormElement = z.infer<typeof NumberFormElement>;

export const CurrencyFormElement = z
  .object({
    elementType: z.literal('currencyInput'),
  })
  .extend(CurrencyField.shape)
  .meta({ description: 'Currency input field.', id: 'CurrencyFormElement' });
export type CurrencyFormElement = z.infer<typeof CurrencyFormElement>;

export const PlainDateFormElement = z
  .object({
    elementType: z.literal('plainDate'),
    ...PlainDateField.shape,
  })
  .meta({ description: 'Date picker input field.', id: 'PlainDateFormElement' });
export type PlainDateFormElement = z.infer<typeof PlainDateFormElement>;

export const BorrowerSelectFormElement = z
  .object({
    elementType: z.literal('borrowerSelect'),
    ...BorrowerField.shape,
  })
  .meta({
    description: 'Select dropdown allowing user to choose from borrowers/contacts associated with the loan.',
    id: 'BorrowerSelectFormElement',
  });
export type BorrowerSelectFormElement = z.infer<typeof BorrowerSelectFormElement>;

export const BooleanFormElement = z
  .object({
    elementType: z.literal('boolean'),
    ...BooleanField.shape,
    presentation: z.union([z.literal('checkbox'), z.literal('radio')]),
    trueLabel: z.string().optional(),
    falseLabel: z.string().optional(),
    // TODO required is tricky on this one in UIs and implies three possible states
    // (unfilled, true, false) -- be careful when implementing UI
  })
  .meta({
    description: 'Boolean input (checkbox or radio) allowing true/false selection.',
    id: 'BooleanFormElement',
  });
export type BooleanFormElement = z.infer<typeof BooleanFormElement>;

export const PresentationOnlyMarkdownFormElement = z
  .object({
    elementType: z.literal('presentationOnlyMarkdown'),

    /**
     * The markdown content.
     *
     * Note: Leading whitespace may be interpreted as a code block -- be careful
     * with template strings (which make it very easy to accidentally add such
     * leading space without realizing)
     */
    markdownContent: z.string(),
  })
  .meta({
    description: 'Markdown content displayed for informational purposes only.',
    id: 'PresentationOnlyMarkdownFormElement',
  });
export type PresentationOnlyMarkdownFormElement = z.infer<typeof PresentationOnlyMarkdownFormElement>;

export const FormGroupElement: z.ZodType<FormGroupElement> = z
  .object({
    elementType: z.literal('group'),
    ...ObjectField.omit({ fields: true }).shape,
    elements: z.tuple(
      [
        z.object({
          key: z.string(),
          element: z.lazy(() => FormElement),
        }),
      ],
      z.object({
        key: z.string(),
        element: z.lazy(() => FormElement),
      }),
    ),
    label: z.string(),
    required: z.boolean(),
    schemaType: z.literal('object'),
  })
  .meta({
    description: 'Container that groups multiple form elements.',
    id: 'FormGroupElement',
  }) satisfies z.ZodType<FormGroupElement>;
export interface FormGroupElement extends Omit<ObjectField, 'fields'> {
  elementType: 'group';
  elements: { key: string; element: FormElement }[];
}

export const PresentationOnlyFormElement = PresentationOnlyMarkdownFormElement;

export const PrimitiveFormElement = z
  .union([
    SelectFormElement,
    RadioFormElement,
    TextInputFormElement,
    TextAreaFormElement,
    EmailFormElement,
    PhoneNumberFormElement,
    BooleanFormElement,
    NumberFormElement,
    CurrencyFormElement,
    PlainDateFormElement,
    BorrowerSelectFormElement,
  ])
  .meta({ id: 'PrimitiveFormElement', description: 'A primitive form element.' });
export type PrimitiveFormElement = z.infer<typeof PrimitiveFormElement>;

export const FormElementWithData = z.union([PrimitiveFormElement, FormGroupElement]).meta({
  id: 'FormElementWithData',
  description: 'A form element with data.',
});
export type FormElementWithData = z.infer<typeof FormElementWithData>;

export const FormElement = z.union([PresentationOnlyFormElement, FormElementWithData]).meta({
  id: 'FormElement',
  description: 'A form element.',
});
export type FormElement = z.infer<typeof FormElement>;

const ProperFormDefinition = z.array(
  z.object({
    key: z.string(),
    element: FormElement,
  }),
);

const LegacyFormDefinition = z.record(z.string().nonempty(), FormElement);

export const Form = ProperFormDefinition.or(
  LegacyFormDefinition.transform(
    (record): Form => Object.entries(record).map(([key, element]) => ({ key, element })),
  ).meta({
    id: 'Form',
    description: 'A form.',
    type: 'object',
  }),
); // FIX LATER - satisfies z.ZodType<Form, z.ZodTypeDef, Form | ObjectForm>;

export type Form = { key: string; element: FormElement }[];

/**
 * ObjectForm was a legacy format for Forms
 *
 * Storage in Postgres as JSONB does not properly preserve the order of the
 * elements so we prefer the array format now. This type & the coercion logic
 * above are provided for backwards compatibility.
 *
 * @deprecated
 */
export type ObjectForm = { [key: string]: FormElement };
